<app-page-header title="User Profile" />

@if (userSig(); as u) {
    <div class="app-page-stack">
        <app-section-card title="Account" icon="pi pi-user">
            <div class="profile-identity">
                <p-avatar
                    class="profile-avatar"
                    shape="circle"
                    size="xlarge"
                    aria-hidden="true"
                    [image]="showPhoto() ? profilePhotoUrl()! : undefined"
                    [label]="showPhoto() ? undefined : userInitials()"
                    (onImageError)="photoFailed()"
                />
                <div class="profile-identity__text">
                    <div class="profile-identity__name">
                        <span>{{ u.displayName }}</span>
                        @if (u.superAdmin) {
                            <p-tag value="Super Admin" icon="pi pi-shield" severity="danger" [rounded]="true" />
                        }
                    </div>
                    @if (isUsingGravatar()) {
                        <a class="profile-identity__link" href="https://gravatar.com" target="_blank" rel="noopener">
                            Change photo on Gravatar<i class="pi pi-external-link" aria-hidden="true"></i>
                        </a>
                    }
                </div>
                @if (canChangePassword()) {
                    <p-button label="Change Password" icon="pi pi-lock" severity="secondary" [outlined]="true" size="small" (onClick)="openPasswordDialog()" />
                }
            </div>

            <dl class="app-facts">
                <div class="app-fact">
                    <dt>Username</dt>
                    <dd>{{ u.username }}</dd>
                </div>
                @if (u.email) {
                    <div class="app-fact">
                        <dt>Email</dt>
                        <dd>{{ u.email }}</dd>
                    </div>
                }
                <div class="app-fact">
                    <dt>Sign-in method</dt>
                    <dd>{{ signInMethod() }}</dd>
                </div>
                <div class="app-fact">
                    <dt>User ID</dt>
                    <dd class="monospace">{{ u.id }}</dd>
                </div>
            </dl>
        </app-section-card>

        <app-section-card
            title="Tenant Permissions"
            subtitle="Tenants you can access and your role in each"
            icon="pi pi-key"
            [count]="tenantsLoading() ? null : tenantPermissions().length"
            [flush]="true"
        >
            @if (tenantsLoading()) {
                <div class="app-card-pad">
                    <app-loading-skeleton preset="list" [count]="u.permissions.length" label="Loading tenant permissions" />
                </div>
            } @else if (tenantPermissions().length) {
                <ul class="app-card-list">
                    @for (perm of tenantPermissions(); track perm.appNamespaceId) {
                        <li class="app-card-list__item">
                            <div class="app-card-list__body">
                                <span class="app-card-list__title">{{ perm.tenantName }}</span>
                                @if (perm.tenantKey) {
                                    <span class="app-card-list__meta"><i class="pi pi-tag" aria-hidden="true"></i>{{ perm.tenantKey }}</span>
                                }
                            </div>
                            <app-role-badge [roleId]="perm.roleId" [roleName]="perm.roleName" />
                        </li>
                    }
                </ul>
            } @else {
                <app-empty-state icon="pi pi-lock" title="No tenant permissions assigned" description="A tenant admin can add you as a member." [compact]="true" />
            }
        </app-section-card>

        <app-connected-apps />
    </div>
}

<!-- Change Password Dialog -->
<p-dialog header="Change Password" [(visible)]="showPasswordDialog" [modal]="true" [style]="{ width: '32rem' }" [breakpoints]="{ '575px': '95vw' }" [draggable]="false" [resizable]="false">
    <div class="flex flex-col gap-4">
        <p-message severity="info" icon="pi pi-info-circle">Password must be at least 6 characters.</p-message>

        <div>
            <label for="profile-current-password" class="block mb-2 font-medium"><i class="pi pi-lock-open mr-2" aria-hidden="true"></i>Current Password</label>
            <p-password inputId="profile-current-password" autocomplete="current-password" [ngModel]="currentPassword()" (ngModelChange)="currentPassword.set($event)" [feedback]="false" [toggleMask]="true" [fluid]="true" placeholder="Enter current password" />
        </div>

        <div>
            <label for="profile-new-password" class="block mb-2 font-medium"><i class="pi pi-key mr-2" aria-hidden="true"></i>New Password</label>
            <p-password inputId="profile-new-password" autocomplete="new-password" [ngModel]="newPassword()" (ngModelChange)="newPassword.set($event)" [feedback]="true" [toggleMask]="true" [fluid]="true" placeholder="Enter new password" />
        </div>

        <div>
            <label for="profile-confirm-password" class="block mb-2 font-medium"><i class="pi pi-check-circle mr-2" aria-hidden="true"></i>Confirm New Password</label>
            <p-password inputId="profile-confirm-password" autocomplete="new-password" [ngModel]="confirmPassword()" (ngModelChange)="confirmPassword.set($event)" [feedback]="false" [toggleMask]="true" [fluid]="true" placeholder="Confirm new password" />
        </div>

        @if (errorSig()) {
            <p-message severity="error" icon="pi pi-times-circle">{{ errorSig() }}</p-message>
        }
        @if (successSig()) {
            <p-message severity="success" icon="pi pi-check-circle">{{ successSig() }}</p-message>
        }
    </div>

    <ng-template #footer>
        <div class="flex gap-3 justify-end">
            <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="closePasswordDialog()" [disabled]="submitting()"></p-button>
            <p-button label="Update Password" icon="pi pi-save" (onClick)="submitChange()" [disabled]="!canSubmit || submitting()" [loading]="submitting()"></p-button>
        </div>
    </ng-template>
</p-dialog>
